import React, { useEffect, useState, Fragment }  from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";
import { Button } from 'semantic-ui-react'
import InfiniteScroll from 'react-infinite-scroll-component';

import useApiService from '../../services/useApiService';
import { debounce, createRiskUrl, getText, getRiskColumnsByLanguage } from '../../helpers';
import LanguageService from '../../services/languageService';

import SemanticUiModal from '../../components/modal';
import ElementLoader from '../../components/element_loader';
import PlansComparisonMenu from './plans_comparison_menu';
import PlansComparisonInScope from './plans_comparison_in_scope';

import './plans_comparison.css'

function PlansComparisonItem(props) {
  const { risk, plansForComparison, currentLanguage, planPermissions } = props;

  const isRiskInPlanScope = (risk, plan) => {
    return plan.risks.find((_risk)=>{
      return _risk.work_phase === risk.work_phase && _risk.activity === risk.activity && _risk.hazard === risk.hazard && _risk.unwanted_event === risk.unwanted_event
    })
  }

  const riskColumns = getRiskColumnsByLanguage(currentLanguage);

  return (
    <tr className="plans-comparison-item">
      <td className="risk-id">{risk.id}</td>
      <td>
        <span className="section">{risk[riskColumns.work_phase] || risk.work_phase_or_ambient_exposure}</span>
        <span className="divider">/</span>
        <span className="section">{risk[riskColumns.activity] || risk.activity}</span>
        <span className="divider">/</span>
        <span className="section">{risk[riskColumns.hazard] || risk.hazard}</span>
        <span className="divider">/</span>
        <span className="section">{risk[riskColumns.unwanted_event] || risk.unwanted_event}</span>
        <span className="divider"></span>
        <span className="section">
          { 
            risk.tag_list.map((tag)=>{
              return <span className="ui label risk-tag">{tag}</span>
            })
          }
        </span>
      </td>
      { 
        plansForComparison && plansForComparison.map((plan)=>{
          const is_in_scope = isRiskInPlanScope(risk, plan);
          const permission = planPermissions.find((permission) => permission.plan_id === plan.id)
          const canUpdate = permission && permission.can_update

          return <td className='in-scope-checkbox'>
            <PlansComparisonInScope
              plan={plan}
              risk={risk}
              isInScope={is_in_scope}
              canUpdate={canUpdate}
            />
            { is_in_scope && <Link className="link text-light-blue" to={createRiskUrl(risk, plan.id)} target="_blank">{getText("View Risk")}</Link>}
          </td>
        })
      }
    </tr>
  )
}

function PlansComparisonHeader() {
  return (
    <div>
      <span>{getText("Compare risks of current plan to other plans")}</span>
    </div>
  )
}

function PlansComparisonContent(props) {
  const { planId: plan_id, translations, currentLanguage, planPermissions } = props;
  const default_plan_ids = plan_id ? [ plan_id ] : [];

  const default_state = {
    page: 1,
    per_page: 20,
    search_term: '',
    risk_tags_filter: [],
    with_unique_description: true,
    include_out_of_scope: true
  }

  const [ state, setState ] = useState(default_state)
  const [ risks, setRisks ] = useState([]);
  const [ selected_plan_ids, setSelectedPlanIds ] = useState(default_plan_ids);
  const [ risksTimer, setRisksTimer] = useState();

  const [{ isLoading: is_plan_dropdown_options_loading, response: plan_dropdown_options }, getPlanDropdownOptions] = useApiService({ url: `/plans`, defaultResponse: [] });
  const [{ isLoading: is_plans_for_comparison_loading, response: plans_for_comparison }, getPlansForComparison] = useApiService({ url: `/plans`, defaultResponse: [] });
  const [{ isLoading: is_risks_loading, response: fetchedRisks }, getRisks] = useApiService({ url: `/risks`, defaultResponse: [] });

  const language_service = new LanguageService;
  const risk_tags_options = language_service.translateRiskTagOptions(translations);

  const setNewPage = () => {
    if (fetchedRisks.length < state.per_page) return;
    setState({ ...state, page: state.page + 1 })
  }

  const onPlanDropdownChange = (_, data) => {
    if (data.value.length === 0) return setSelectedPlanIds(default_plan_ids);
    setSelectedPlanIds(data.value)
  }

  const onSearchChange = (_, data) => {
    setState({ ...state, page: 1, search_term: data.value })
  }

  const onRiskTagsChange = (_, data) => {
    setState({ ...state, page: 1, risk_tags_filter: data.value })
  }

  const debouncedGetRisks = () => {
    let newRisksTimer = debounce(getRisks, 1000, risksTimer)({ params: state });
    setRisksTimer(newRisksTimer)
  }

  useEffect(()=>{
    debouncedGetRisks()
  }, [state.page, state.search_term, state.risk_tags_filter])

  useEffect(()=>{
    if (state.page === 1) return setRisks(fetchedRisks);
    setRisks(risks.concat(fetchedRisks));
  }, [fetchedRisks])

  useEffect(()=>{
    const plan_options_params = {
      is_template: false,
      disregard_permissions: true,
      return_format: 'plans_for_dropdown_options'
    }
    getPlanDropdownOptions({ params: plan_options_params });
  }, [])

  useEffect(()=>{
    const plans_for_comparison_params = {
      is_template: false,
      plan_ids: selected_plan_ids,
      disregard_permissions: true,
      return_format: 'plans_for_comparison'
    }
    getPlansForComparison({ params: plans_for_comparison_params });
  }, [selected_plan_ids])

  return (
    <Fragment>
      <PlansComparisonMenu
        planDropdownOptions={plan_dropdown_options}
        selectedPlanIds={selected_plan_ids}
        searchTerm={state.search_term}
        riskTagsOptions={risk_tags_options}
        selectedRiskTags={state.risk_tags_filter}
        onPlanDropdownChange={onPlanDropdownChange}
        onSearchChange={onSearchChange}
        onRiskTagsChange={onRiskTagsChange}
      />
      <InfiniteScroll
        dataLength={risks.length}
        next={setNewPage}
        hasMore={true}
        height={1}
      >
        <table className="plans-comparison-table table">
          <thead>
            <tr>
              <th className="risk-id-header">{getText("Risk ID")}</th>
              <th className="risk-details-header">{getText("Risk Details")}</th>
              { 
                plans_for_comparison && plans_for_comparison.map((plan)=>{
                  const permission = planPermissions.find((permission) => permission.plan_id === plan.id)
                  const canUpdate = permission && permission.can_update
                  return (
                    <th className="plan-name-header">
                      <div>{plan.name}</div>
                      {
                        canUpdate &&
                        <div className='red-text permission'>(Edit)</div>
                      }
                      {
                        !canUpdate &&
                        <div className='blue-text permission'>(View)</div>
                      }
                    </th>
                  )
                })

              }
            </tr>
          </thead>
          {
            risks && risks.length ?
            <tbody>
              {
                risks.map((risk, index)=>{
                  return (
                    <PlansComparisonItem 
                      key={index}
                      risk={risk}
                      plansForComparison={plans_for_comparison}
                      currentLanguage={currentLanguage}
                      planPermissions={planPermissions}
                    />
                  )
                })
              } 
            </tbody>
            :
            <tbody>
              <tr>
                <td colspan="3" className='no-records'>{getText("No related records found.")}</td>
              </tr>
            </tbody>
          }
          <ElementLoader isLoading={is_risks_loading} message={`${getText("Fetching related risks")}...`} />
          <ElementLoader isLoading={is_plans_for_comparison_loading} message={`${getText("Fetching selected plans for comparison")}...`} />
          <ElementLoader isLoading={is_plan_dropdown_options_loading} message={`${getText("Fetching all available plans")}...`} />
        </table>
      </InfiniteScroll>
    </Fragment>
  )
}

function PlansComparisonActions(props) {
  return (
    <div>
      <Button fluid size='medium' className="new-plan-button" onClick={props.hidePlansComparison}>
        {getText("Close")}
      </Button>
    </div>
  )
}

function PlansComparison(props) {
  const { plan, hidePlansComparison, translations, currentLanguage, planPermissions } = props
  const modalOptions = { className: "plans-comparison", size: "tiny" }

  return (
    <SemanticUiModal
      modalOptions={modalOptions}
      header={<PlansComparisonHeader/>}
      content={<PlansComparisonContent planId={plan.id} translations={translations} currentLanguage={currentLanguage} planPermissions={planPermissions} />}
      actions={<PlansComparisonActions hidePlansComparison={hidePlansComparison}/>}
      closeModal={hidePlansComparison}
    />
  )
}

const mapStateToProps = (state) => {
  return {
    translations: state.language.language.translations,
    currentLanguage: state.language.language.name,
    planPermissions: state.app_token.user.plan_permissions
  }
}

export default connect(mapStateToProps, {})(PlansComparison);



// WEBPACK FOOTER //
// ./src/views/plans_comparison/plans_comparison.js